import { Distribution, DistributionParam, GraphDto } from "@custom-types/graphs"
import { GraphStoreType } from "@features/graph/Graph"
import {
  GraphNodeData,
  GraphNodeEdgeData,
  GroupNodeData,
  UnpackedGraph,
} from "@store/services/converter/types"
import { Node } from "reactflow"
import { GraphDataViewModel } from "@features/graph/graphViewModel"
import semver from "semver/preload"
import { GraphDetails } from "@features/builder/types"
import { format } from "date-fns"
import logger from "@utils/logger"

export const getNodeLabel = (node: Node<GraphNodeData | GroupNodeData | GraphNodeEdgeData>) => {
  return node.data.labelMap[node.id]
}

export const getGroupNodesLabels = (node: Node<GroupNodeData>) => {
  return node.data.group.nodes.map((nodeId) => [nodeId, node.data.labelMap[nodeId]])
}

export const getGroupedNodesForGroup = (node: Node<GroupNodeData>): [string, GraphNodeData[]][] => {
  const nodes: GraphNodeData[] = node.data.group.nodes
    .map((nodeId) => GraphDataViewModel.data?.nodeMap[nodeId])
    .filter((x) => x != undefined) as GraphNodeData[]
  return Object.entries(
    Object.groupBy(nodes, (node: GraphNodeData) => {
      return node.display.groups?.at(-1) || "No Group"
    }),
  ) as [string, GraphNodeData[]][]
}

export const getDirectNodesOfGroup = (groupId: string) => {
  const group = GraphDataViewModel.data?.groupMap[groupId]
  if (!GraphDataViewModel.data || !group) return []

  const allNestedNodesIds = group.group.nodes
  const childrenGroupsIds = GraphDataViewModel.data.childrenGroupMap[group.id] ?? []
  const childrenGroupsNodesIds = new Set<string>()

  for (const childrenGroupId of childrenGroupsIds) {
    const childrenGroup = GraphDataViewModel.data.groupMap[childrenGroupId]
    if (!childrenGroup) continue

    childrenGroup.group.nodes.forEach((id) => childrenGroupsNodesIds.add(id))
  }

  const directNodesIds = allNestedNodesIds.filter((nodeId) => !childrenGroupsNodesIds.has(nodeId))

  const directNodes: GraphNodeData[] = []
  for (const nodeId of directNodesIds) {
    const node = GraphDataViewModel.data.nodeMap[nodeId]
    if (node) directNodes.push(node)
  }

  return directNodes
}

export const getNotGroupedNodes = () => {
  if (!GraphDataViewModel.data) return []

  const allNodesIds = Object.keys(GraphDataViewModel.data.nodeMap)

  const mainGroups = Object.values(GraphDataViewModel.data.groupMap).filter(
    (group) => !group.group.parent,
  )
  const allGroupedNodesIds = new Set<string>()

  for (const mainGroup of mainGroups) {
    mainGroup.group.nodes.forEach((id) => allGroupedNodesIds.add(id))
  }

  const notGroupedNodesIds = allNodesIds.filter((nodeId) => !allGroupedNodesIds.has(nodeId))

  const notGroupedNodes: GraphNodeData[] = []
  for (const nodeId of notGroupedNodesIds) {
    const node = GraphDataViewModel.data.nodeMap[nodeId]
    if (node) notGroupedNodes.push(node)
  }

  return notGroupedNodes
}

export const isEqual = (graph1: UnpackedGraph, graph2: UnpackedGraph) => {
  const graph1Ordered = { graph: graph1.graph, xy: graph1.xy, meta: graph1.meta }
  const graph2Ordered = { graph: graph2.graph, xy: graph2.xy, meta: graph2.meta }

  return JSON.stringify(graph1Ordered) === JSON.stringify(graph2Ordered)
}

export const graphKeyToFamilyKey = (graphKey: string): string => {
  const graphKeyArr = graphKey.split("#")
  const graphVersion = graphKeyArr.pop()
  if (!graphVersion) return ""
  const graphVersionArr = graphVersion.split("_")
  graphVersionArr.pop()
  return `${graphKeyArr.join("#")}#${graphVersionArr.join("_")}`
}

export const graphKeyToVersion = (graphKey: string): string => {
  const graphKeyArr = graphKey.split("#")
  const graphVersion = graphKeyArr.pop()
  if (!graphVersion) return ""
  return graphVersion.split("_").pop() || ""
}

// searches only in visible nodes. It won't work if you're looking for node inside collapsed group
export const findNode = (graphStore: GraphStoreType, id: string) => {
  return graphStore
    .getState()
    .getNodes()
    .find((node) => node.id === id)
}

export const getSortedGraphBases = (bases: GraphDto[]) =>
  [...bases].sort((baseA, baseB) => {
    try {
      return semver.compare(baseB.version, baseA.version)
    } catch (error) {
      return -1
    }
  })

export type DistributionMap = Partial<Record<Distribution, DistributionParam[]>>

export const continuousNodeDistributionProperties: DistributionMap = {
  [Distribution.Weibull]: [
    DistributionParam.c,
    DistributionParam.loc,
    DistributionParam.scale,
    DistributionParam.lbound,
    DistributionParam.ubound,
  ],
  [Distribution.Wald]: [
    DistributionParam.loc,
    DistributionParam.scale,
    DistributionParam.lbound,
    DistributionParam.ubound,
  ],
  [Distribution.Vonmises]: [
    DistributionParam.kappa,
    DistributionParam.loc,
    DistributionParam.scale,
    DistributionParam.lbound,
    DistributionParam.ubound,
  ],
  [Distribution.Uniform]: [
    DistributionParam.loc,
    DistributionParam.scale,
    DistributionParam.lbound,
    DistributionParam.ubound,
  ],
  [Distribution.Triangular]: [
    DistributionParam.c,
    DistributionParam.loc,
    DistributionParam.scale,
    DistributionParam.lbound,
    DistributionParam.ubound,
  ],
  [Distribution.T]: [
    DistributionParam.df,
    DistributionParam.loc,
    DistributionParam.scale,
    DistributionParam.lbound,
    DistributionParam.ubound,
  ],
  [Distribution.Rayleigh]: [
    DistributionParam.loc,
    DistributionParam.scale,
    DistributionParam.lbound,
    DistributionParam.ubound,
  ],
  [Distribution.Power]: [
    DistributionParam.a,
    DistributionParam.loc,
    DistributionParam.scale,
    DistributionParam.lbound,
    DistributionParam.ubound,
  ],
  [Distribution.Pareto]: [
    DistributionParam.b,
    DistributionParam.loc,
    DistributionParam.scale,
    DistributionParam.lbound,
    DistributionParam.ubound,
  ],
  [Distribution.Normal]: [
    DistributionParam.loc,
    DistributionParam.scale,
    DistributionParam.lbound,
    DistributionParam.ubound,
  ],
  [Distribution.NonCentralF]: [
    DistributionParam.dfn,
    DistributionParam.dfd,
    DistributionParam.nc,
    DistributionParam.loc,
    DistributionParam.scale,
    DistributionParam.lbound,
    DistributionParam.ubound,
  ],
  [Distribution.NonCentralChisquare]: [
    DistributionParam.df,
    DistributionParam.nc,
    DistributionParam.loc,
    DistributionParam.scale,
    DistributionParam.lbound,
    DistributionParam.ubound,
  ],
  [Distribution.Lognormal]: [
    DistributionParam.s,
    DistributionParam.loc,
    DistributionParam.scale,
    DistributionParam.lbound,
    DistributionParam.ubound,
  ],
  [Distribution.Logistic]: [
    DistributionParam.loc,
    DistributionParam.scale,
    DistributionParam.lbound,
    DistributionParam.ubound,
  ],
  [Distribution.Laplace]: [
    DistributionParam.loc,
    DistributionParam.scale,
    DistributionParam.lbound,
    DistributionParam.ubound,
  ],
  [Distribution.Gumbel]: [
    DistributionParam.loc,
    DistributionParam.scale,
    DistributionParam.lbound,
    DistributionParam.ubound,
  ],
  [Distribution.Gamma]: [
    DistributionParam.a,
    DistributionParam.loc,
    DistributionParam.scale,
    DistributionParam.lbound,
    DistributionParam.ubound,
  ],
  [Distribution.F]: [
    DistributionParam.dfn,
    DistributionParam.dfd,
    DistributionParam.loc,
    DistributionParam.scale,
    DistributionParam.lbound,
    DistributionParam.ubound,
  ],
  [Distribution.Exponential]: [
    DistributionParam.loc,
    DistributionParam.scale,
    DistributionParam.lbound,
    DistributionParam.ubound,
  ],
  [Distribution.Chisquare]: [
    DistributionParam.df,
    DistributionParam.loc,
    DistributionParam.scale,
    DistributionParam.lbound,
    DistributionParam.ubound,
  ],
  [Distribution.Cauchy]: [
    DistributionParam.loc,
    DistributionParam.scale,
    DistributionParam.lbound,
    DistributionParam.ubound,
  ],
  [Distribution.Beta]: [
    DistributionParam.a,
    DistributionParam.b,
    DistributionParam.loc,
    DistributionParam.scale,
    DistributionParam.lbound,
    DistributionParam.ubound,
  ],
}

export const discreteNodeDistributionProperties: DistributionMap = {
  [Distribution.Binomial]: [
    DistributionParam.p,
    DistributionParam.n,
    DistributionParam.loc,
    DistributionParam.lbound,
    DistributionParam.ubound,
  ],
  [Distribution.Geometric]: [
    DistributionParam.p,
    DistributionParam.loc,
    DistributionParam.lbound,
    DistributionParam.ubound,
  ],
  [Distribution.Hypergeometric]: [
    DistributionParam.M,
    DistributionParam.N,
    DistributionParam.n,
    DistributionParam.loc,
    DistributionParam.lbound,
    DistributionParam.ubound,
  ],
  [Distribution.Logseries]: [
    DistributionParam.p,
    DistributionParam.loc,
    DistributionParam.lbound,
    DistributionParam.ubound,
  ],
  [Distribution.Poisson]: [
    DistributionParam.mu,
    DistributionParam.loc,
    DistributionParam.lbound,
    DistributionParam.ubound,
  ],
}

export const categoricalNodeDistributionProperties: { [Distribution.Categorical]: string[] } = {
  [Distribution.Categorical]: [], // user adds / removes params
}

export const distributionProperties = {
  ...continuousNodeDistributionProperties,
  ...discreteNodeDistributionProperties,
  ...categoricalNodeDistributionProperties,
}

/**
 * This function combines both of this objects to get the most updated GraphDto object.
 *
 * @param details - GraphDetails is object that we get from socket (yjs graph document) and it is "working" draft of graph for user.
 * @param graph - GraphDto is object that we get from GET HTTP requests, but it is updated only on major bumps or after sampling.
 */
export const getUpdatedGraphDto = (details: GraphDetails, graph: GraphDto): GraphDto => {
  const [organisationKey, divisionKey, productKey] = details.Product.split("#")

  return {
    key: `${details.Product}#${details.Graph}_${details.Version}`,
    name: details.Graph,
    product_key: productKey!,
    product_organisation_key: organisationKey!,
    product_division_key: divisionKey!,
    base_graph_family_key: `${details.Product}#${details.Graph.replaceAll(" ", "-")}`,
    version: details.Version,
    scenarios: details.Scenarios ?? [],
    scenario_source: details.ScenarioSource,
    scenario_version: details.ScenarioVersion ?? "",
    dashboard_s3_location: details.DashboardS3Location ?? "",
    dashboard_s3_hash: details.DashboardS3Hash ?? "",
    start_date:
      !details.StartDate || details.StartDate === "rolling"
        ? "rolling"
        : format(new Date(details.StartDate!), "yyyy-MM-dd"),
    end_date:
      !details.EndDate || details.EndDate === "rolling"
        ? "rolling"
        : format(new Date(details.EndDate!), "yyyy-MM-dd"),
    yearly_rolling_start_month:
      details.YearlyRollingStartMonth === "" ? null : Number(details.YearlyRollingStartMonth),
    simulation_frequency: details.SimulationFrequency,

    // missing properties in GraphDetails will be taken directly from GraphDto
    rolling_duration: graph.rolling_duration,
    published: graph.published,
    owner_group_key: graph.owner_group_key,
    creator_email: graph.creator_email,
    creation_date: graph.creation_date,
    last_updated: graph.last_updated,
    parametrizable: graph.parametrizable,
    sampler_version: graph.sampler_version,
    description: graph.description,
    s3_location: graph.s3_location,
    s3_hash: graph.s3_hash,
  }
}

export const getNormalisedGraphKey = (graphDetails: GraphDetails) => {
  const normalisedName = graphDetails.Graph.replaceAll(" ", "-").replaceAll("#", "")
  return `${graphDetails.Product.replaceAll(" ", "-")}#${normalisedName}_${graphDetails.Version}`
}

export const getGraphVersionFromGraphKey = (key: string) => {
  const splittedKey = key.split("_")

  const version = splittedKey[splittedKey.length - 1]

  if (!version) {
    logger.error(`Failed to extract graph version from graph key "${key}"`)
    return ""
  }

  return version
}
